// What? A component to display search results in a table format
// Why? To show company information in an organized, paginated table
// How? Renders a table with company data and pagination controls

import React from "react";
import { Sheet, Table, Link, Card, Stack, Box, Typography } from "@mui/joy";

import PageLoading from "../../../components/PageLoading";
import { HeaderCell } from "../components/HeaderCell";
import { CompanyOperatingStatus } from "../components/CompanyOperatingStatus";
import AutodbTooltipWrapper, {
  AutodbTooltipHost,
} from "../components/AutodbTooltipWrapper";
import { useWideLayout } from "../hooks/useWideLayout";
import {
  getFieldInfo,
  formatHeadquartersAddress,
  formatLatestFunding,
  getDevelopmentStage,
  getLeadPipelineAsset,
  getScalarFieldValue,
  getTherapeuticAreas,
} from "../utils/autodbValues";

interface SearchResultsProps {
  loading?: boolean;
  results: any[];
  onCompanyClick: (companyId: string) => void;
  /** When provided, company names render as real anchors so Cmd/Ctrl/middle-click opens a new tab. */
  hrefForCompany?: (companyId: string) => string;
  showAttendance?: boolean;
  /** Attached to the first result row so the tutorial overlay can extend its spotlight to include it. */
  firstRowRef?: React.Ref<HTMLTableRowElement>;
}

export const SearchResults: React.FC<SearchResultsProps> = ({
  loading = false,
  results,
  onCompanyClick,
  hrefForCompany,
  showAttendance = false,
  firstRowRef,
}) => {
  const isWide = useWideLayout();

  const hasAttendance = (results: any[]): boolean => {
    return results.some((company) => !!company?.value?.attendance?.length);
  };

  const renderLatestFunding = (latest_funding: any) =>
    formatLatestFunding(latest_funding);

  const companyRowFields = (company: any) => {
    const lead_asset = getLeadPipelineAsset(company);
    return {
      name: getScalarFieldValue(company?.value?.name),
      latest_funding:
        company?.value?.latest_funding?.value ?? company?.value?.latest_funding,
      lead_asset,
      tas: getTherapeuticAreas(lead_asset),
      address: formatHeadquartersAddress(company),
      developmentStage: getDevelopmentStage(lead_asset),
      employees: getScalarFieldValue(company?.value?.number_of_employees),
    };
  };

  const renderAttendance = (events: any) => {
    if (!events || events.length === 0) return <></>;

    return (
      <td>
        {events.map((e: any, index: number) => (
          <div
            key={index}
            style={{ whiteSpace: "nowrap", paddingBottom: "0.75em" }}
          >
            {e?.value?.person?.value?.name?.value && (
              <div>
                <strong>{e?.value?.person?.value?.name?.value}</strong>
              </div>
            )}
            <AutodbTooltipWrapper info={e?.value?.person?.value?.role}>
              {e?.value?.person?.value?.role?.value && (
                <div> - {e?.value?.person?.value?.role?.value}</div>
              )}
            </AutodbTooltipWrapper>
            <AutodbTooltipWrapper info={e?.value?.person?.value?.email}>
              {e?.value?.person?.value?.email?.value && (
                <div> - {e?.value?.person?.value?.email?.value}</div>
              )}
            </AutodbTooltipWrapper>
          </div>
        ))}
      </td>
    );
  };

  const renderAttendanceCompact = (events: any) => {
    if (!events || events.length === 0) return null;

    return (
      <Box>
        {events.map((e: any, index: number) => (
          <Box key={index} sx={{ mb: 1 }}>
            {e?.value?.person?.value?.name?.value && (
              <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                {e?.value?.person?.value?.name?.value}
              </Typography>
            )}
            <AutodbTooltipWrapper info={e?.value?.person?.value?.role}>
              {e?.value?.person?.value?.role?.value && (
                <Typography level="body-xs" color="neutral">
                  {e?.value?.person?.value?.role?.value}
                </Typography>
              )}
            </AutodbTooltipWrapper>
            <AutodbTooltipWrapper info={e?.value?.person?.value?.email}>
              {e?.value?.person?.value?.email?.value && (
                <Typography level="body-xs" color="neutral">
                  {e?.value?.person?.value?.email?.value}
                </Typography>
              )}
            </AutodbTooltipWrapper>
          </Box>
        ))}
      </Box>
    );
  };

  const renderCompany = (company: any, index: number) => {
    const {
      name,
      latest_funding,
      lead_asset,
      tas,
      address,
      developmentStage,
      employees,
    } = companyRowFields(company);

    return (
      <tr key={company._id} ref={index === 0 ? firstRowRef : undefined}>
        <td>
          <Stack spacing={0.5} alignItems="flex-start">
            <Link
              href={hrefForCompany ? hrefForCompany(company._id) : undefined}
              component={hrefForCompany ? "a" : "button"}
              onClick={(
                e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
              ) => {
                // Let modified clicks (Cmd/Ctrl/Shift/middle) fall through to native anchor
                if (
                  hrefForCompany &&
                  (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1)
                )
                  return;
                e.preventDefault();
                onCompanyClick(company._id);
              }}
              sx={{
                fontWeight: 500,
                textAlign: "left",
              }}
            >
              {name ? name : ""}
            </Link>
            <CompanyOperatingStatus company={company} />
          </Stack>
        </td>
        <td>
          <AutodbTooltipWrapper info={company?.value?.headquarters}>
            {address ?? ""}
          </AutodbTooltipWrapper>
        </td>
        <td>
          <AutodbTooltipWrapper
            info={getFieldInfo(lead_asset, "therapeutic_area")}
          >
            {tas?.join(", ") ?? ""}
          </AutodbTooltipWrapper>
        </td>
        <td>
          <AutodbTooltipWrapper
            info={getFieldInfo(lead_asset, "development_stage")}
          >
            {developmentStage}
          </AutodbTooltipWrapper>
        </td>
        <td>
          <AutodbTooltipWrapper info={company?.value?.number_of_employees}>
            {employees ?? ""}
          </AutodbTooltipWrapper>
        </td>
        <td>
          <AutodbTooltipWrapper info={company?.value?.latest_funding}>
            {latest_funding ? renderLatestFunding(latest_funding) : ""}
          </AutodbTooltipWrapper>
        </td>
        <td>{company?.value?.hubspot_owner?.value ?? ""}</td>
        {showAttendance && renderAttendance(company?.value?.attendance)}
      </tr>
    );
  };

  // A render function, not a component: declared in here, a component would be
  // a new type on every render, remounting every card and pulling the source
  // tooltip off the icon it was open on.
  const renderCompanyCard = (company: any) => {
    const {
      name,
      latest_funding,
      lead_asset,
      tas,
      address,
      developmentStage,
      employees,
    } = companyRowFields(company);

    return (
      <Card
        key={company._id}
        variant="outlined"
        sx={{
          p: 2,
          transition: "all 0.2s ease",
          "&:hover": {
            boxShadow: "sm",
            borderColor: "primary.300",
          },
        }}
      >
        <Stack spacing={1.5}>
          {/* Company Name */}
          <Box>
            <Link
              href={hrefForCompany ? hrefForCompany(company._id) : undefined}
              component={hrefForCompany ? "a" : "button"}
              onClick={(
                e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
              ) => {
                // Let modified clicks (Cmd/Ctrl/Shift/middle) fall through to native anchor
                if (
                  hrefForCompany &&
                  (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1)
                )
                  return;
                e.preventDefault();
                onCompanyClick(company._id);
              }}
              sx={{
                fontWeight: 600,
                fontSize: "1rem",
                textAlign: "left",
              }}
            >
              {name || "Unnamed Company"}
            </Link>
            <CompanyOperatingStatus company={company} />
          </Box>

          {/* Location */}
          {address && (
            <Box>
              <Typography
                level="body-xs"
                color="neutral"
                sx={{ fontWeight: 600, mb: 0.5 }}
              >
                Location
              </Typography>
              <AutodbTooltipWrapper info={company?.value?.headquarters}>
                <Typography level="body-sm">{address}</Typography>
              </AutodbTooltipWrapper>
            </Box>
          )}

          {/* Therapeutic Areas */}
          {tas && tas.length > 0 && (
            <Box>
              <Typography
                level="body-xs"
                color="neutral"
                sx={{ fontWeight: 600, mb: 0.5 }}
              >
                Therapeutic Areas
              </Typography>
              <AutodbTooltipWrapper
                info={getFieldInfo(lead_asset, "therapeutic_area")}
              >
                <Typography level="body-sm">{tas.join(", ")}</Typography>
              </AutodbTooltipWrapper>
            </Box>
          )}

          {/* Development Stage and Employee Count */}
          <Stack direction="row" spacing={3}>
            {developmentStage && (
              <Box sx={{ flex: 1 }}>
                <Typography
                  level="body-xs"
                  color="neutral"
                  sx={{ fontWeight: 600, mb: 0.5 }}
                >
                  Development Stage
                </Typography>
                <AutodbTooltipWrapper
                  info={getFieldInfo(lead_asset, "development_stage")}
                >
                  <Typography level="body-sm">{developmentStage}</Typography>
                </AutodbTooltipWrapper>
              </Box>
            )}
            {employees && (
              <Box sx={{ flex: 1 }}>
                <Typography
                  level="body-xs"
                  color="neutral"
                  sx={{ fontWeight: 600, mb: 0.5 }}
                >
                  Employees
                </Typography>
                <AutodbTooltipWrapper
                  info={company?.value?.number_of_employees}
                >
                  <Typography level="body-sm">{employees}</Typography>
                </AutodbTooltipWrapper>
              </Box>
            )}
          </Stack>

          {/* Last Funding */}
          {latest_funding && renderLatestFunding(latest_funding) && (
            <Box>
              <Typography
                level="body-xs"
                color="neutral"
                sx={{ fontWeight: 600, mb: 0.5 }}
              >
                Last Funding
              </Typography>
              <AutodbTooltipWrapper info={company?.value?.latest_funding}>
                <Typography level="body-sm">
                  {renderLatestFunding(latest_funding)}
                </Typography>
              </AutodbTooltipWrapper>
            </Box>
          )}

          {/* HubSpot Owner */}
          {company?.value?.hubspot_owner?.value && (
            <Box>
              <Typography
                level="body-xs"
                color="neutral"
                sx={{ fontWeight: 600, mb: 0.5 }}
              >
                HubSpot Owner
              </Typography>
              <Typography level="body-sm">
                {company.value.hubspot_owner.value}
              </Typography>
            </Box>
          )}

          {/* Attendance */}
          {showAttendance &&
            company?.value?.attendance &&
            company.value.attendance.length > 0 && (
              <Box>
                <Typography
                  level="body-xs"
                  color="neutral"
                  sx={{ fontWeight: 600, mb: 0.5 }}
                >
                  Attendance
                </Typography>
                {renderAttendanceCompact(company?.value?.attendance)}
              </Box>
            )}
        </Stack>
      </Card>
    );
  };

  if (loading) {
    return <PageLoading />;
  }

  return (
    <>
      {/* One tooltip serves every wrapped cell on this page. */}
      <AutodbTooltipHost />

      {/* Only the live layout mounts -- see useWideLayout. */}
      {isWide ? (
        <Sheet sx={{ overflow: "auto" }}>
          <Table sx={{ width: "100%" }}>
            <thead>
              <tr>
                <HeaderCell>Company Name</HeaderCell>
                <HeaderCell>Location</HeaderCell>
                <HeaderCell>Therapeutic Areas</HeaderCell>
                <HeaderCell>Development Stage</HeaderCell>
                <HeaderCell>Employee Count</HeaderCell>
                <HeaderCell>Last Funding</HeaderCell>
                <HeaderCell>HubSpot Owner</HeaderCell>
                {showAttendance && hasAttendance(results) && (
                  <HeaderCell>Attendance</HeaderCell>
                )}
              </tr>
            </thead>
            <tbody>{results.map((c, i) => renderCompany(c, i))}</tbody>
          </Table>
        </Sheet>
      ) : (
        <Stack spacing={2}>{results.map(renderCompanyCard)}</Stack>
      )}
    </>
  );
};
